<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import { useNotifications } from '@/composables/useNotifications'
import Button from 'primevue/button'
import Menu from 'primevue/menu'
import OverlayPanel from 'primevue/overlaypanel'
import Badge from 'primevue/badge'

const { t } = useI18n()
const authStore = useAuthStore()
const uiStore = useUiStore()

// Notifications
const {
  notifications,
  unreadCount,
  hasUnread,
  isConnected,
  initialize: initNotifications,
  markAsRead,
  markAllAsRead,
} = useNotifications()

const userMenu = ref()
const modeMenu = ref()
const notificationPanel = ref()

const userName = computed(() => authStore.userName)
const userRole = computed(() => authStore.userRole)
const roleLabel = computed(() => t(`roles.${userRole.value}`))

const userMenuItems = ref([
  {
    label: t('settings.profile'),
    icon: 'pi pi-user',
    command: () => {
      // Navigate to settings
    }
  },
  {
    label: t('settings.title'),
    icon: 'pi pi-cog',
    command: () => {
      // Navigate to settings
    }
  },
  { separator: true },
  {
    label: t('auth.logout'),
    icon: 'pi pi-sign-out',
    command: () => authStore.logout()
  }
])

// Mode items - Intent and Wordless only for patients
const modeItems = computed(() => {
  const items = [
    {
      label: t('modes.classic'),
      icon: 'pi pi-desktop',
      command: () => uiStore.setMode('classic')
    }
  ]

  // Only patients can use Intent and Wordless modes
  if (authStore.isPatient) {
    items.push(
      {
        label: t('modes.intent'),
        icon: 'pi pi-microphone',
        command: () => uiStore.setMode('intent')
      },
      {
        label: t('modes.wordless'),
        icon: 'pi pi-image',
        command: () => uiStore.setMode('wordless')
      }
    )
  }

  return items
})

function toggleUserMenu(event: Event) {
  userMenu.value.toggle(event)
}

function toggleModeMenu(event: Event) {
  modeMenu.value.toggle(event)
}

function toggleNotifications(event: Event) {
  notificationPanel.value.toggle(event)
}

function formatNotificationTime(date: string): string {
  const d = new Date(date)
  const now = new Date()
  const diff = now.getTime() - d.getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 1) return t('time.justNow')
  if (minutes < 60) return t('time.minutesAgo', { count: minutes })
  if (hours < 24) return t('time.hoursAgo', { count: hours })
  return t('time.daysAgo', { count: days })
}

function getPriorityClass(priority: number): string {
  if (priority >= 8) return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
  if (priority >= 5) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
  return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
}

// Initialize notifications when user is authenticated
onMounted(async () => {
  if (authStore.isAuthenticated) {
    await initNotifications()
  }
})

// Re-initialize if auth state changes
watch(() => authStore.isAuthenticated, async (isAuth) => {
  if (isAuth) {
    await initNotifications()
  }
})
</script>

<template>
  <header class="fixed top-0 left-0 right-0 h-16 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 z-30">
    <div class="h-full px-4 flex items-center justify-between">
      <!-- Left: Menu button + Logo -->
      <div class="flex items-center gap-4">
        <Button
          icon="pi pi-bars"
          text
          rounded
          @click="uiStore.toggleSidebar"
          class="text-gray-600 dark:text-gray-300"
        />

        <div class="flex items-center gap-2">
          <svg viewBox="0 0 40 40" class="w-8 h-8">
            <circle cx="20" cy="20" r="18" fill="#4CAF50" opacity="0.2"/>
            <path
              d="M20 8 L20 32 M12 16 L20 8 L28 16"
              stroke="#4CAF50"
              stroke-width="3"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <h1 class="text-xl font-bold text-gray-900 dark:text-white hide-in-wordless">
            DiabAssist
          </h1>
        </div>
      </div>

      <!-- Right: Actions -->
      <div class="flex items-center gap-2">
        <!-- Mode selector (only for patients who have multiple modes) -->
        <template v-if="authStore.isPatient">
          <Button
            :icon="uiStore.mode === 'classic' ? 'pi pi-desktop' : uiStore.mode === 'intent' ? 'pi pi-microphone' : 'pi pi-image'"
            text
            rounded
            @click="toggleModeMenu"
            v-tooltip.bottom="t('modes.selectMode')"
            class="text-gray-600 dark:text-gray-300"
          />
          <Menu ref="modeMenu" :model="modeItems" popup />
        </template>

        <!-- Dark mode toggle -->
        <Button
          :icon="uiStore.isDark ? 'pi pi-sun' : 'pi pi-moon'"
          text
          rounded
          @click="uiStore.toggleDark"
          class="text-gray-600 dark:text-gray-300"
        />

        <!-- Notifications -->
        <div class="relative">
          <Button
            icon="pi pi-bell"
            text
            rounded
            @click="toggleNotifications"
            :class="[
              'text-gray-600 dark:text-gray-300',
              { 'animate-pulse': hasUnread }
            ]"
          />
          <Badge
            v-if="unreadCount > 0"
            :value="unreadCount > 9 ? '9+' : unreadCount"
            severity="danger"
            class="absolute -top-1 -right-1"
          />
          <span
            v-if="isConnected"
            class="absolute bottom-0 right-0 w-2 h-2 bg-green-500 rounded-full border border-white"
            v-tooltip.bottom="t('notifications.connected')"
          />
        </div>

        <!-- Notification Panel -->
        <OverlayPanel ref="notificationPanel" class="w-80 max-w-full">
          <div class="flex items-center justify-between mb-3">
            <h3 class="font-semibold text-gray-900 dark:text-white">
              {{ t('notifications.title') }}
            </h3>
            <Button
              v-if="hasUnread"
              :label="t('notifications.markAllRead')"
              text
              size="small"
              @click="markAllAsRead"
            />
          </div>

          <div v-if="notifications.length === 0" class="text-center py-4 text-gray-500">
            {{ t('notifications.empty') }}
          </div>

          <div v-else class="space-y-2 max-h-80 overflow-y-auto">
            <div
              v-for="notif in notifications.slice(0, 10)"
              :key="notif.id"
              :class="[
                'p-3 rounded-lg cursor-pointer transition-colors',
                notif.read
                  ? 'bg-gray-50 dark:bg-gray-800'
                  : 'bg-primary-50 dark:bg-primary-900/20',
              ]"
              @click="markAsRead(notif.id)"
            >
              <div class="flex items-start justify-between gap-2">
                <div class="flex-1 min-w-0">
                  <p class="font-medium text-sm text-gray-900 dark:text-white truncate">
                    {{ notif.title }}
                  </p>
                  <p class="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                    {{ notif.message }}
                  </p>
                </div>
                <span
                  v-if="notif.priority >= 8"
                  :class="['px-1.5 py-0.5 rounded text-xs font-medium', getPriorityClass(notif.priority)]"
                >
                  {{ t('notifications.urgent') }}
                </span>
              </div>
              <p class="text-xs text-gray-400 mt-1">
                {{ formatNotificationTime(notif.date) }}
              </p>
            </div>
          </div>
        </OverlayPanel>

        <!-- User menu -->
        <div class="flex items-center gap-2 ml-2">
          <div class="text-right hidden sm:block">
            <p class="text-sm font-medium text-gray-900 dark:text-white hide-in-wordless">
              {{ userName }}
            </p>
            <span :class="['role-badge', `role-${userRole}`]">
              {{ roleLabel }}
            </span>
          </div>

          <Button
            icon="pi pi-user"
            rounded
            @click="toggleUserMenu"
            class="bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300"
          />
          <Menu ref="userMenu" :model="userMenuItems" popup />
        </div>
      </div>
    </div>
  </header>
</template>
